import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { useSitesInfo } from '@newfold/huapi-js';
import { SitesInfo200 } from '@newfold/huapi-js/src/index.schemas';

import Breadcrumbs from '~/components/Breadcrumbs';
import { useMFEContext } from '~/components/MFEProvider';
import useAMRevamp from '~/hooks/useAMRevamp';
import useHostingInfo from '~/hooks/useHostingInfo';
import useLocalStorage from '~/hooks/useLocalStorage';
import usePromotionInfo from '~/hooks/usePromotionInfo';
import useSitePath from '~/hooks/useSitePath';
import { SiteDetailLocationState } from '~/types/site';
import { getSiteDisabled, isSiteIdAvailable } from '~/utils/site/helpers';

type SiteContextTypes = UseQueryResult<
  AxiosResponse<SitesInfo200, any>,
  AxiosError<unknown, any>
> & {
  data?: SitesInfo200;
  accountId?: string | null | undefined;
  isDisabled?: boolean;
  isHostingDisabled?: boolean;
  isSiteOrHostingDisabled?: boolean;
  promotionInProgress: boolean;
  isSiteResetting: boolean;
};

export const SiteContext = createContext({} as SiteContextTypes);
export const useSiteContext = () => useContext(SiteContext);

export default function SiteDetail() {
  const { t } = useTranslation('site');
  const { siteId } = useParams();
  // @ts-expect-error
  const { hostingId } = useMFEContext();
  const { isAMRevamp } = useAMRevamp();
  const [currentAMLeftNav] = useLocalStorage('HOSTING_TYPE', 'HOSTING', true);

  const location = useLocation();
  const { siteReturnTo: routeReturnTo, siteReturnLabel: routeReturnLabel } =
    (location.state as SiteDetailLocationState | null) ?? {};
  const [persistedReturn, setPersistedReturn] =
    useState<SiteDetailLocationState>({});

  useEffect(() => {
    setPersistedReturn({});
  }, [siteId]);

  useEffect(() => {
    if (routeReturnTo || routeReturnLabel) {
      setPersistedReturn({
        siteReturnTo: routeReturnTo,
        siteReturnLabel: routeReturnLabel,
      });
    }
  }, [routeReturnTo, routeReturnLabel]);

  const { isValid } = useSitePath(siteId);

  const { data: siteInfo, ...site } = useSitesInfo(Number(siteId), {
    query: {
      enabled: isValid && isSiteIdAvailable(siteId),
      select: (data) => ({
        ...data?.data,
      }),
    },
  });

  const { inProgress } = usePromotionInfo(siteInfo, site?.isFetching);

  const isDisabled = getSiteDisabled(siteInfo?.status) || site?.isPending;

  // Note: We are blocking the API call because it was failing during a site reset
  const isSiteResetting =
    siteInfo?.status === 'reset' || siteInfo?.status === 'pending_reset';

  const accountBackRef = siteInfo?.account_back_ref;
  const siteAccountId =
    !!accountBackRef && accountBackRef.startsWith('WN')
      ? siteInfo?.account_back_ref
      : siteInfo?.account_id;

  const {
    isDisabled: hostingDisabled,
    isPending: hostingLoading,
    isSuspended: hostingSuspended,
    isDecommissioned: hostingDecommissioned,
  } = useHostingInfo({
    hostingId: siteAccountId,
    options: {
      enabled: !!siteAccountId,
    },
  });

  // if the user's hosting is disabled,
  // they should not be able to access the sites detail pages
  if (
    !hostingLoading &&
    (hostingDisabled || hostingSuspended || hostingDecommissioned)
  )
    return <Navigate to=".." />;

  type SiteContextTypes = {
    data: SitesInfo200;
    accountId: string | null | undefined;
    isDisabled: boolean;
    isHostingDisabled: boolean;
    isSiteOrHostingDisabled: boolean;
    promotionInProgress: boolean;
    isSiteResetting: boolean;
  };
  const siteData = {
    data: { ...siteInfo },
    ...site,
    accountId: siteAccountId,
    isDisabled,
    isHostingDisabled: hostingDisabled || hostingLoading,
    isSiteOrHostingDisabled: isDisabled || hostingDisabled || hostingLoading,
    promotionInProgress: inProgress,
    isSiteResetting: isSiteResetting,
  } as SiteContextTypes;

  const siteReturnTo = routeReturnTo ?? persistedReturn.siteReturnTo;
  const siteReturnLabel = routeReturnLabel ?? persistedReturn.siteReturnLabel;
  const navigateTo =
    siteReturnTo ??
    (currentAMLeftNav === 'WEBSITE'
      ? '/sites'
      : `/package/websites?packageId=${hostingId}`);
  const breadcrumbParentLabel =
    siteReturnLabel ?? t('websites', { defaultValue: 'WEBSITES' });
  const siteName = siteInfo?.name;

  return (
    // @ts-expect-error
    <SiteContext.Provider value={siteData}>
      {/* <Box marginBottom={3}>
        <MonarxAlerts />
      </Box> */}
      {isAMRevamp && siteName && (
        <Breadcrumbs
          levels={[
            { label: String(breadcrumbParentLabel), navigateTo },
            { label: siteName, navigateTo: '#' },
          ]}
        />
      )}
      <Outlet />
    </SiteContext.Provider>
  );
}
